import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { FormControl, FormHelperText } from '../shared/FormControl';
import { useFormContext } from 'react-hook-form';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  helperText?: string;
  error?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
  grow?: boolean; // Enable dynamic height growth
  classNames?: { textarea?: string; label?: string };
  dataTestId?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      helperText,
      error,
      variant = 'standard',
      fullWidth = false,
      grow = false,
      required,
      disabled,
      id,
      placeholder,
      classNames,
      value,
      onChange,
      onFocus,
      onBlur,
      dataTestId,
      ...props
    },
    forwardedRef
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const [focused, setFocused] = useState(false); // Local focus state for manual control if no React Hook Form
    const [filled, setFilled] = useState(!!value || !!props.defaultValue);
    const { setFocus } = useFormContext() || {}; // Safely check if useFormContext is available
    const resizeTextarea = () => {
      if (grow && textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (!setFocus) {
        // If React Hook Form's setFocus is not available, use local state
        setFocused(true);
      }
      onFocus?.(e); // Call onFocus from React Hook Form or custom logic
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (!setFocus) {
        // If React Hook Form's setFocus is not available, use local state
        setFocused(false);
      }
      onBlur?.(e); // Call onBlur from React Hook Form or custom logic

      if (setFocus && id) {
        setFocus(id); // Trigger React Hook Form's setFocus if it's available
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (grow) resizeTextarea();
      onChange?.(e);
    };

    useEffect(() => {
      if (grow) resizeTextarea();
    }, [value, grow]);

    const textareaStyles = cn(
      'block w-full text-md transition-colors focus:outline-none bg-transparent',
      {
        'resize-none overflow-hidden': grow,
        'resize-y': !grow,
        'border rounded-md focus:ring-1 hover:ring-1 ring-neutral-100': variant === 'outlined',
        'border-b-2 focus:border-b-2 hover:border-b-neutral-100': variant === 'filled' || variant === 'standard',
        'border-gray-300 focus:border-primary-500 focus:ring-primary-500': !error,
        'border-critical focus:border-critical focus:ring-critical': error,
        'text-gray-500 cursor-not-allowed': disabled,
      },
      variant === 'outlined' && 'px-2 pt-6 pb-2',
      variant === 'filled' && 'px-2 pt-6 pb-2 bg-gray-100',
      variant === 'standard' && 'px-2 pt-4 pb-2',
      fullWidth && 'w-full',
      classNames?.textarea,
      className
    );

    const labelStyles = cn(
      'absolute left-0.5 top-3 text-gray-500 transition-all duration-150 pointer-events-none origin-left scale-100',
      {
        'scale-75 -translate-y-4 translate-x-1': focused || filled,
        'text-primary-500': focused,
        'text-gray-500 top-0': disabled,
        'text-critical': error,
      },
      classNames?.label
    );

    return (
      <FormControl error={error}>
        <div className="relative m-2">
          <label htmlFor={id} className={labelStyles}>
            {label}
            {required && <span className="text-critical ml-1">*</span>}
          </label>
          <textarea
            ref={(el) => {
              textareaRef.current = el;
              if (typeof forwardedRef === 'function') {
                forwardedRef(el);
              } else if (forwardedRef && 'current' in forwardedRef) {
                forwardedRef.current = el;
              }
            }}
            className={textareaStyles}
            id={id}
            disabled={disabled}
            required={required}
            aria-invalid={error}
            aria-describedby={helperText ? `${id}-helper-text` : undefined}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            value={value}
            rows={1}
            data-testid={dataTestId}
            {...props}
          />
        </div>
        {helperText && (
          <FormHelperText id={`${id}-helper-text`} className={`px-4 ${error && 'text-critical'}`} error={error}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }
);

Textarea.displayName = 'Textarea';
